// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useGetRidehailsQuery } from '../queries/getRidehails.query.generated';
import { toRidehailRows, useRidehails } from '@hopdrive/feature-ridehails';
import { useRooftop, useUser } from '@store';
import dayjs from 'dayjs';

import RideSharesTabContent from './RideSharesTabContent';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';

// COMPONENT ---------------------------------------------------------------- //

const RideSharesTabQuery: React.FC = () => {
  const rooftop = useRooftop();
  const user = useUser();
  const { setData, setLoading } = useRidehails();

  // Get customerId from the selected rooftop, falling back to the user's own customer.
  const [customerId, setCustomerId] = React.useState<number>(rooftop?.id || user?.profile?.customer_id || 0);

  // Update customerId when rooftop changes
  React.useEffect(() => {
    let newCustomerId: number = 0;
    if (rooftop?.id) {
      newCustomerId = rooftop.id;
    } else if (user?.profile?.customer_id) {
      newCustomerId = user.profile.customer_id;
    }
    setCustomerId(newCustomerId);
  }, [rooftop, user]);

  // Fetch data for the last 90 days, then filter client-side
  const ninetyDaysAgo = dayjs().subtract(90, 'days').startOf('day').toISOString();
  const tomorrow = dayjs().add(1, 'day').endOf('day').toISOString();

  const { data, loading, error, refetch } = useGetRidehailsQuery({
    variables: {
      customer_id: customerId,
      from_date: ninetyDaysAgo,
      to_date: tomorrow,
      limit: 1000,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !customerId,
  });

  React.useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  React.useEffect(() => {
    // The shared package owns the row shape; the "not called" placeholder and the field mapping live there.
    if (data?.moves) setData(toRidehailRows(data.moves));
  }, [data, setData]);

  if (error) {
    return (
      // The app fallback takes no title or refetch; the title folds into the message it does render.
      <DefaultFallback severity='error' message={`Error loading rideshares: ${error.message}`} />
    );
  }

  return <RideSharesTabContent refetch={refetch} />;
};

// EXPORT ---------------------------------------------------------------- //

export default RideSharesTabQuery;